import { useEffect, useState, type FormEvent } from 'react';
import Modal, { inputCls, labelCls } from '@/components/base/Modal';
import {
  fetchCommissionPayments,
  addCommissionPayment,
  deleteCommissionPayment,
  type Commission,
  type CommissionPayment,
} from '@/services/commissions';

interface CommissionPaymentsModalProps {
  commission: Commission;
  onClose: () => void;
  onSaved: () => void;
}

// Sistema de abonos para comisiones: permite registrar pagos parciales a un
// vendedor hasta cubrir el total de su comisión. Muestra lo pagado y lo
// pendiente en tiempo real.
export default function CommissionPaymentsModal({
  commission,
  onClose,
  onSaved,
}: CommissionPaymentsModalProps) {
  const [payments, setPayments] = useState<CommissionPayment[]>([]);
  const [loading, setLoading] = useState(true);
  const [amount, setAmount] = useState('');
  const [paidAt, setPaidAt] = useState(() => new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function load() {
    setLoading(true);
    const list = await fetchCommissionPayments(commission.id);
    setPayments(list);
    setLoading(false);
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commission.id]);

  const paidTotal = payments.reduce((s, p) => s + p.amount, 0);
  const pending = Math.max(0, commission.commissionAmount - paidTotal);

  async function handleAdd(e: FormEvent) {
    e.preventDefault();
    setError('');
    if ((Number(amount) || 0) <= 0) {
      setError('El monto debe ser mayor a cero.');
      return;
    }
    setSaving(true);
    const { error: addError } = await addCommissionPayment({
      commissionId: commission.id,
      amount: Number(amount) || 0,
      paidAt: paidAt || '',
      notes,
    });
    setSaving(false);
    if (addError) {
      setError(addError);
      return;
    }
    setAmount('');
    setNotes('');
    await load();
    onSaved();
  }

  async function handleDelete(id: string) {
    const { error: delError } = await deleteCommissionPayment(id, commission.id);
    if (!delError) {
      await load();
      onSaved();
    }
  }

  function formatDate(value: string) {
    if (!value) return '—';
    return new Date(value + 'T00:00:00').toLocaleDateString('es-CO', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  }

  return (
    <Modal
      title="Abonos de comisión"
      subtitle={`${commission.salespersonName || '—'} · Comisión: $${commission.commissionAmount.toLocaleString()}`}
      onClose={onClose}
      maxWidth="max-w-lg"
    >
      <div className="space-y-4">
        {/* Resumen pagado / pendiente */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-3">
            <p className="text-xs text-emerald-700">Pagado</p>
            <p className="text-lg font-bold text-emerald-700 mt-0.5">${paidTotal.toLocaleString()}</p>
          </div>
          <div className="bg-amber-50 border border-amber-100 rounded-lg p-3">
            <p className="text-xs text-amber-700">Por pagar</p>
            <p className="text-lg font-bold text-amber-700 mt-0.5">${pending.toLocaleString()}</p>
          </div>
        </div>

        <form onSubmit={handleAdd} className="space-y-3 bg-background-50 border border-background-200 rounded-lg p-4">
          <p className="text-xs font-semibold text-foreground-700">Registrar un abono</p>
          {error && (
            <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2.5">
              <i className="ri-error-warning-line"></i>
              <span>{error}</span>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Monto ($) *</label>
              <input type="number" min="0" className={inputCls} value={amount} onChange={(e) => setAmount(e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Fecha</label>
              <input type="date" className={inputCls} value={paidAt} onChange={(e) => setPaidAt(e.target.value)} />
            </div>
          </div>
          <div>
            <label className={labelCls}>Nota</label>
            <input className={inputCls} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Transferencia, efectivo, etc." maxLength={200} />
          </div>
          <div className="flex justify-end">
            <button type="submit" disabled={saving} className="text-sm px-4 py-2.5 bg-accent-500 text-white rounded-md hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed">
              {saving ? 'Guardando...' : 'Agregar abono'}
            </button>
          </div>
        </form>

        <div>
          <p className="text-xs font-semibold text-foreground-700 mb-2">Historial de abonos ({payments.length})</p>
          {loading ? (
            <div className="text-center py-6 text-foreground-400 text-sm">
              <i className="ri-loader-4-line animate-spin mr-2"></i>Cargando...
            </div>
          ) : payments.length === 0 ? (
            <div className="text-center py-6 text-foreground-400 text-sm">
              Aún no hay abonos registrados.
            </div>
          ) : (
            <div className="space-y-2 max-h-[280px] overflow-y-auto">
              {payments.map((p) => (
                <div key={p.id} className="flex items-center gap-3 bg-white border border-background-200 rounded-lg px-3 py-2.5">
                  <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
                    <i className="ri-cash-line text-emerald-600"></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground-900">${p.amount.toLocaleString()}</p>
                    <p className="text-xs text-foreground-500 truncate">
                      {formatDate(p.paidAt)}
                      {p.notes ? ` · ${p.notes}` : ''}
                    </p>
                  </div>
                  <button
                    onClick={() => handleDelete(p.id)}
                    className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-400 hover:bg-red-50 hover:text-red-600 transition-colors cursor-pointer"
                    aria-label="Eliminar"
                  >
                    <i className="ri-delete-bin-line"></i>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}